import { createAppForm, useFieldContext, useFormContext } from '@awpmarine/ui/components/form';
import { Alert, AlertDescription } from '@awpmarine/ui/components/ui/alert';
import { Checkbox } from '@awpmarine/ui/components/ui/checkbox';
import { Field, FieldError, FieldLabel } from '@awpmarine/ui/components/ui/field';
import { Input } from '@awpmarine/ui/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@awpmarine/ui/components/ui/select';
import { Spinner } from '@awpmarine/ui/components/ui/spinner';
import { Textarea } from '@awpmarine/ui/components/ui/textarea';
import { cn } from 'cn';
import { CircleAlertIcon } from 'lucide-react';

// The website's field and form components, built into its form hooks by the shared `createAppForm`.

export { submitWith, useSelector } from '@awpmarine/ui/components/form';

function TextField({
  id,
  label,
  className,
  ...props
}: Omit<React.ComponentProps<typeof Input>, 'name' | 'value' | 'onChange' | 'onBlur' | 'className'> & {
  label: React.ReactNode;
  /** Sizes the field in the form's grid. */
  className?: string;
}) {
  const field = useFieldContext<string>();
  const invalid = !field.state.meta.isValid;

  return (
    <Field data-invalid={invalid || undefined} className={className}>
      <FieldLabel htmlFor={id ?? field.name} className='text-base font-semibold'>
        {label}
      </FieldLabel>
      <Input
        id={id ?? field.name}
        name={field.name}
        value={field.state.value}
        onBlur={field.handleBlur}
        onChange={(event) => field.handleChange(event.target.value)}
        aria-invalid={invalid || undefined}
        className='h-12.5 rounded-xl bg-white px-4 text-base shadow-none md:text-base'
        {...props}
      />
      {invalid && <FieldError errors={field.state.meta.errors} />}
    </Field>
  );
}

function TextareaField({
  id,
  label,
  className,
  ...props
}: Omit<React.ComponentProps<typeof Textarea>, 'name' | 'value' | 'onChange' | 'onBlur' | 'className'> & {
  label: React.ReactNode;
  /** Sizes the field in the form's grid. */
  className?: string;
}) {
  const field = useFieldContext<string>();
  const invalid = !field.state.meta.isValid;

  return (
    <Field data-invalid={invalid || undefined} className={className}>
      <FieldLabel htmlFor={id ?? field.name} className='text-base font-semibold'>
        {label}
      </FieldLabel>
      <Textarea
        id={id ?? field.name}
        name={field.name}
        value={field.state.value}
        onBlur={field.handleBlur}
        onChange={(event) => field.handleChange(event.target.value)}
        aria-invalid={invalid || undefined}
        rows={6}
        className='min-h-36 rounded-xl bg-white px-4 py-3 text-base shadow-none md:text-base'
        {...props}
      />
      {invalid && <FieldError errors={field.state.meta.errors} />}
    </Field>
  );
}

/** A choice from a fixed list, labelled by `options` in the page's language, so it is never invalid. */
function SelectField<T extends string>({
  id,
  label,
  options,
  className,
}: {
  id?: string;
  label: React.ReactNode;
  options: Record<T, () => string>;
  className?: string;
}) {
  const field = useFieldContext<T>();
  return (
    <Field className={className}>
      <FieldLabel htmlFor={id ?? field.name} className='text-base font-semibold'>
        {label}
      </FieldLabel>
      <Select<T> value={field.state.value} onValueChange={(value) => value !== null && field.handleChange(value)}>
        <SelectTrigger
          id={id ?? field.name}
          className='h-12.5 w-full rounded-xl bg-white px-4 text-base shadow-none data-[size=default]:h-12.5 md:text-base'
        >
          <SelectValue>{(value: T) => options[value]()}</SelectValue>
        </SelectTrigger>
        <SelectContent alignItemWithTrigger={false} sideOffset={6}>
          {(Object.keys(options) as T[]).map((value) => (
            <SelectItem key={value} value={value} className='py-2.5 text-base'>
              {options[value]()}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

function CheckboxField({ id, label, className }: { id?: string; label: React.ReactNode; className?: string }) {
  const field = useFieldContext<boolean>();
  const invalid = !field.state.meta.isValid;

  return (
    <Field data-invalid={invalid || undefined} className={className}>
      <div className='text-body flex items-start gap-3 leading-normal'>
        <Checkbox
          id={id ?? field.name}
          name={field.name}
          checked={field.state.value}
          onCheckedChange={(checked) => field.handleChange(checked)}
          aria-invalid={invalid || undefined}
          className='mt-0.5 size-5'
        />
        <label htmlFor={id ?? field.name}>{label}</label>
      </div>
      {invalid && <FieldError errors={field.state.meta.errors} />}
    </Field>
  );
}

function SubmitButton({ children }: { children: React.ReactNode }) {
  const form = useFormContext();
  return (
    <form.Subscribe selector={(state) => state.isSubmitting}>
      {(submitting) => (
        <button
          type='submit'
          disabled={submitting}
          className='inline-flex min-h-12 items-center justify-center gap-2.5 rounded-full bg-brand px-7 font-semibold text-white transition-colors hover:bg-[#1f4f8a] disabled:opacity-70'
        >
          {submitting ? <Spinner /> : children}
        </button>
      )}
    </form.Subscribe>
  );
}

function FormError({ className }: { className?: string }) {
  const form = useFormContext();
  return (
    <form.Subscribe selector={(state) => state.errorMap.onSubmit}>
      {(error) =>
        typeof error === 'string' && (
          <Alert variant='destructive' className={cn('rounded-xl bg-white px-4 py-3 text-base', className)}>
            <CircleAlertIcon />
            <AlertDescription className='text-base'>{error}</AlertDescription>
          </Alert>
        )
      }
    </form.Subscribe>
  );
}

export const { useSchemaForm } = createAppForm({
  fieldComponents: { TextField, TextareaField, SelectField, CheckboxField },
  formComponents: { SubmitButton, FormError },
});
